import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TopBar, Field, Section } from "@/components/Shell";
import { LogoFrame, ClientPlaceholderMark } from "@/components/Logos";
import { CANDIDATE } from "@/constants/testIds";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Building2, CheckCircle2, Loader2, Plus, Trash2, Upload, X } from "lucide-react";

const STEPS = [
  { key: "personal", label: "Personal" },
  { key: "professional", label: "Professional" },
  { key: "bank", label: "Payroll" },
  { key: "immigration", label: "Immigration" },
  { key: "vehicle", label: "Vehicle & Insurance" },
  { key: "review", label: "Review" },
];

const initialData = {
  // Vehicle & Insurance (ephemeral File refs — sent as base64 on submit)
  truck_picture_file: null,
  insurance_certificate_file: null,
  insurance_expiry: "",
  drivers_license_file: null,
  drivers_license_expiry: "",

  // Assignment
  project: "",
  role: "",

  full_name: "",
  email: "",
  phone: "",
  date_of_birth: "",
  gender: "",
  nationality: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state_region: "",
  postal_code: "",
  country: "",
  emergency_contact_name: "",
  emergency_contact_relation: "",
  emergency_contact_phone: "",

  years_experience: 0,
  ftth_experience_years: 0,
  skills: [],
  certifications: [],
  previous_employer: "",
  previous_role: "",
  driver_license_number: "",
  has_own_tools: false,

  bank_name: "",
  bank_account_holder: "",
  bank_account_number: "",
  bank_routing_number: "",
  bank_iban: "",
  bank_swift: "",
  tax_id: "",

  immigration_status: "citizen",
  immigration_notes: "",
  work_authorization_expiry: "",

  documents: [],
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function ApplyPage() {
  const nav = useNavigate();
  const { slug } = useParams();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [submitting, setSubmitting] = useState(false);

  // Client + catalog loaded on mount
  const [intake, setIntake] = useState(null); // { client_name, slug, description } or null
  const [intakeError, setIntakeError] = useState("");
  const [choices, setChoices] = useState({ projects: [], roles: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data: cat } = await api.get("/catalog/choices");
        setChoices(cat);
      } catch (err) {
        console.error("Failed to load project/role catalog:", err);
      }
      if (slug) {
        try {
          const { data: it } = await api.get(`/intakes/by-slug/${slug}`);
          setIntake(it);
        } catch (e) {
          setIntakeError(formatApiError(e));
        }
      }
    })();
  }, [slug]);

  // draft helpers for skills / certs / docs
  const [skillDraft, setSkillDraft] = useState("");
  const [certDraft, setCertDraft] = useState({ name: "", issuer: "", year: "" });
  const [docDraft, setDocDraft] = useState({
    kind: "passport",
    label: "",
    expiry_date: "",
    file: null,
  });

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const stepErrors = useMemo(() => validateStep(step, data), [step, data]);
  const canNext = stepErrors.length === 0;

  const next = () => {
    if (!canNext) {
      toast.error(stepErrors[0]);
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addSkill = () => {
    const v = skillDraft.trim();
    if (!v) return;
    if (data.skills.includes(v)) return setSkillDraft("");
    set({ skills: [...data.skills, v] });
    setSkillDraft("");
  };
  const removeSkill = (i) => set({ skills: data.skills.filter((_, idx) => idx !== i) });

  const addCert = () => {
    if (!certDraft.name.trim()) return;
    set({ certifications: [...data.certifications, { ...certDraft }] });
    setCertDraft({ name: "", issuer: "", year: "" });
  };
  const removeCert = (i) =>
    set({ certifications: data.certifications.filter((_, idx) => idx !== i) });

  const addDoc = async () => {
    if (!docDraft.file) return toast.error("Choose a file");
    if (!docDraft.label.trim()) return toast.error("Add a document label");
    const file = docDraft.file;
    if (file.size > 5 * 1024 * 1024) return toast.error("Max file size is 5MB");
    const data_base64 = await fileToBase64(file);
    set({
      documents: [
        ...data.documents,
        {
          kind: docDraft.kind,
          label: docDraft.label.trim(),
          file_name: file.name,
          mime_type: file.type || "application/octet-stream",
          data_base64,
          expiry_date: docDraft.expiry_date || null,
        },
      ],
    });
    setDocDraft({ kind: "passport", label: "", expiry_date: "", file: null });
    const el = document.getElementById("apply-doc-file-input");
    if (el) el.value = "";
    toast.success("Document attached");
  };
  const removeDoc = (i) => set({ documents: data.documents.filter((_, idx) => idx !== i) });

  const submit = async () => {
    setSubmitting(true);
    try {
      const vehicleDocs = [];
      if (data.truck_picture_file) {
        const f = data.truck_picture_file;
        vehicleDocs.push({
          kind: "truck_picture",
          label: "Truck picture",
          file_name: f.name,
          mime_type: f.type || "application/octet-stream",
          data_base64: await fileToBase64(f),
        });
      }
      if (data.insurance_certificate_file) {
        const f = data.insurance_certificate_file;
        vehicleDocs.push({
          kind: "insurance_certificate",
          label: "Insurance certificate",
          file_name: f.name,
          mime_type: f.type || "application/octet-stream",
          data_base64: await fileToBase64(f),
          expiry_date: data.insurance_expiry || null,
        });
      }
      if (data.drivers_license_file) {
        const f = data.drivers_license_file;
        vehicleDocs.push({
          kind: "drivers_license",
          label: "Driver's license",
          file_name: f.name,
          mime_type: f.type || "application/octet-stream",
          data_base64: await fileToBase64(f),
          expiry_date: data.drivers_license_expiry || null,
        });
      }
      const { truck_picture_file, insurance_certificate_file, ...rest } = data;
      const payload = {
        ...rest,
        documents: [...data.documents, ...vehicleDocs],
        intake_slug: slug || null,
        years_experience: Number(data.years_experience) || 0,
        ftth_experience_years: Number(data.ftth_experience_years) || 0,
        work_authorization_expiry: data.work_authorization_expiry || null,
      };
      const { data: res } = await api.post("/candidates", payload);
      nav(`/apply/success?ref=${encodeURIComponent(res.reference)}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <TopBar mode="public" />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
        {/* Client banner */}
        {slug && (
          <div
            data-testid={CANDIDATE.clientBanner}
            className="brut-card p-4 mb-6 flex items-start gap-4"
            style={{ background: "rgb(var(--ink))", color: "rgb(var(--paper))" }}
          >
            <LogoFrame
              src={intake?.logo_data_url}
              alt={intake ? `${intake.client_name} logo` : "Client logo"}
              size={56}
              fallback={
                <div className="w-14 h-14 border-2 border-[rgb(var(--paper))] bg-[rgb(var(--flame))] grid place-items-center text-black">
                  <Building2 size={22} />
                </div>
              }
            />
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-70">
                Recruiting on behalf of
              </div>
              {intakeError ? (
                <div className="text-sm mt-1 text-[rgb(var(--flame))]">{intakeError}</div>
              ) : intake ? (
                <>
                  <div className="font-display text-2xl mt-0.5">{intake.client_name}</div>
                  {intake.description && (
                    <div className="text-sm opacity-80 mt-1">{intake.description}</div>
                  )}
                </>
              ) : (
                <div className="text-sm mt-1 opacity-70">Loading client…</div>
              )}
            </div>
          </div>
        )}
        {!slug && (
          <div className="brut-card p-3 mb-6 text-sm font-mono uppercase tracking-widest text-black/70">
            Open call · Magna Mentes
          </div>
        )}

        <Section
          kicker={`Step ${step + 1} of ${STEPS.length}`}
          title={
            step === 0 ? "Tell us who you are"
            : step === 1 ? "Your fiber field experience"
            : step === 2 ? "Payroll & tax details"
            : step === 3 ? "Work authorization & proofs"
            : step === 4 ? "Vehicle & insurance"
            : "Review & submit"
          }
        />

        {/* Stepper */}
        <div className="flex flex-wrap gap-3 mb-8">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              data-testid={CANDIDATE.stepIndicator(i)}
              className={`pill ${i === step ? "pill-ink" : i < step ? "pill-success" : "pill-muted"}`}
            >
              {i < step ? <CheckCircle2 size={12} /> : <span className="font-mono">{i + 1}</span>}
              {s.label}
            </div>
          ))}
        </div>

        <div className="brut-card p-6 md:p-8">
          {step === 0 && <StepPersonal data={data} set={set} choices={choices} />}
          {step === 1 && (
            <StepProfessional
              data={data}
              set={set}
              skillDraft={skillDraft}
              setSkillDraft={setSkillDraft}
              addSkill={addSkill}
              removeSkill={removeSkill}
              certDraft={certDraft}
              setCertDraft={setCertDraft}
              addCert={addCert}
              removeCert={removeCert}
            />
          )}
          {step === 2 && <StepBank data={data} set={set} />}
          {step === 3 && (
            <StepImmigration
              data={data}
              set={set}
              docDraft={docDraft}
              setDocDraft={setDocDraft}
              addDoc={addDoc}
              removeDoc={removeDoc}
            />
          )}
          {step === 4 && <StepVehicle data={data} set={set} />}
          {step === 5 && <StepReview data={data} />}
        </div>

        <div className="flex items-center justify-between mt-6">
          <button
            onClick={back}
            disabled={step === 0}
            data-testid={CANDIDATE.backBtn}
            className="btn-brut btn-ghost"
          >
            <ArrowLeft size={16} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} data-testid={CANDIDATE.nextBtn} className="btn-brut btn-flame">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid={CANDIDATE.submitBtn}
              className="btn-brut btn-flame"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
              Submit application
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- STEPS ----------

function StepPersonal({ data, set, choices }) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="md:col-span-2">
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] mb-3">
          Assignment
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Project" required>
            <select
              className="select-brut"
              data-testid={CANDIDATE.project}
              value={data.project}
              onChange={(e) => set({ project: e.target.value })}
            >
              <option value="">Select a project…</option>
              {choices.projects.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Field / role" required>
            <select
              className="select-brut"
              data-testid={CANDIDATE.role}
              value={data.role}
              onChange={(e) => set({ role: e.target.value })}
            >
              <option value="">Select a field…</option>
              {choices.roles.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="dashed-line my-6" />
      </div>

      <Field label="Full name" required>
        <input
          className="input-brut"
          data-testid={CANDIDATE.fullName}
          value={data.full_name}
          onChange={(e) => set({ full_name: e.target.value })}
          placeholder="Jane Doe"
        />
      </Field>
      <Field label="Email" required>
        <input
          type="email"
          className="input-brut"
          data-testid={CANDIDATE.email}
          value={data.email}
          onChange={(e) => set({ email: e.target.value })}
          placeholder="jane@example.com"
        />
      </Field>
      <Field label="Phone" required>
        <input
          className="input-brut"
          data-testid={CANDIDATE.phone}
          value={data.phone}
          onChange={(e) => set({ phone: e.target.value })}
          placeholder="+1 555 000 1234"
        />
      </Field>
      <Field label="Date of birth" required>
        <input
          type="date"
          className="input-brut"
          data-testid={CANDIDATE.dob}
          value={data.date_of_birth}
          onChange={(e) => set({ date_of_birth: e.target.value })}
        />
      </Field>
      <Field label="Gender">
        <select
          className="select-brut"
          value={data.gender}
          onChange={(e) => set({ gender: e.target.value })}
        >
          <option value="">—</option>
          <option value="female">Female</option>
          <option value="male">Male</option>
          <option value="non_binary">Non-binary</option>
          <option value="prefer_not_to_say">Prefer not to say</option>
        </select>
      </Field>
      <Field label="Nationality" required>
        <input
          className="input-brut"
          data-testid={CANDIDATE.nationality}
          value={data.nationality}
          onChange={(e) => set({ nationality: e.target.value })}
          placeholder="e.g. Nigerian"
        />
      </Field>

      <Field label="Address line 1" required>
        <input
          className="input-brut"
          data-testid={CANDIDATE.address1}
          value={data.address_line1}
          onChange={(e) => set({ address_line1: e.target.value })}
        />
      </Field>
      <Field label="Address line 2">
        <input
          className="input-brut"
          value={data.address_line2}
          onChange={(e) => set({ address_line2: e.target.value })}
        />
      </Field>
      <Field label="City" required>
        <input
          className="input-brut"
          data-testid={CANDIDATE.city}
          value={data.city}
          onChange={(e) => set({ city: e.target.value })}
        />
      </Field>
      <Field label="State / Region">
        <input
          className="input-brut"
          value={data.state_region}
          onChange={(e) => set({ state_region: e.target.value })}
        />
      </Field>
      <Field label="Postal code">
        <input
          className="input-brut"
          value={data.postal_code}
          onChange={(e) => set({ postal_code: e.target.value })}
        />
      </Field>
      <Field label="Country" required>
        <input
          className="input-brut"
          data-testid={CANDIDATE.country}
          value={data.country}
          onChange={(e) => set({ country: e.target.value })}
        />
      </Field>

      <div className="md:col-span-2 pt-2">
        <div className="dashed-line mb-6" />
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] mb-3">
          Emergency contact
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <Field label="Name" required>
            <input
              className="input-brut"
              data-testid={CANDIDATE.emergencyName}
              value={data.emergency_contact_name}
              onChange={(e) => set({ emergency_contact_name: e.target.value })}
            />
          </Field>
          <Field label="Relation" required>
            <input
              className="input-brut"
              data-testid={CANDIDATE.emergencyRelation}
              value={data.emergency_contact_relation}
              onChange={(e) => set({ emergency_contact_relation: e.target.value })}
              placeholder="Spouse, Sibling…"
            />
          </Field>
          <Field label="Phone" required>
            <input
              className="input-brut"
              data-testid={CANDIDATE.emergencyPhone}
              value={data.emergency_contact_phone}
              onChange={(e) => set({ emergency_contact_phone: e.target.value })}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

function StepProfessional({
  data, set, skillDraft, setSkillDraft, addSkill, removeSkill,
  certDraft, setCertDraft, addCert, removeCert,
}) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Field label="Total years of field experience" required>
        <input
          type="number"
          min={0}
          className="input-brut"
          data-testid={CANDIDATE.yearsExp}
          value={data.years_experience}
          onChange={(e) => set({ years_experience: e.target.value })}
        />
      </Field>
      <Field label="FTTH / fiber-specific experience (years)" required>
        <input
          type="number"
          min={0}
          className="input-brut"
          data-testid={CANDIDATE.ftthYears}
          value={data.ftth_experience_years}
          onChange={(e) => set({ ftth_experience_years: e.target.value })}
        />
      </Field>

      <Field label="Previous employer">
        <input
          className="input-brut"
          data-testid={CANDIDATE.previousEmployer}
          value={data.previous_employer}
          onChange={(e) => set({ previous_employer: e.target.value })}
        />
      </Field>
      <Field label="Previous role">
        <input
          className="input-brut"
          data-testid={CANDIDATE.previousRole}
          value={data.previous_role}
          onChange={(e) => set({ previous_role: e.target.value })}
          placeholder="e.g. Fusion Splicer"
        />
      </Field>

      <Field label="Driver's license number">
        <input
          className="input-brut"
          data-testid={CANDIDATE.driverLicense}
          value={data.driver_license_number}
          onChange={(e) => set({ driver_license_number: e.target.value })}
        />
      </Field>
      <label className="flex items-end gap-3 pb-3">
        <input
          type="checkbox"
          className="w-5 h-5 border-2 border-black accent-black"
          data-testid={CANDIDATE.hasTools}
          checked={data.has_own_tools}
          onChange={(e) => set({ has_own_tools: e.target.checked })}
        />
        <span className="text-sm">I have my own splicing / OSP tools</span>
      </label>

      {/* Skills */}
      <div className="md:col-span-2">
        <div className="dashed-line mb-6" />
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] mb-3">Skills</div>
        <div className="flex gap-3">
          <input
            className="input-brut flex-1"
            data-testid={CANDIDATE.skillInput}
            placeholder="Fusion splicing, OTDR, aerial climbing…"
            value={skillDraft}
            onChange={(e) => setSkillDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())}
          />
          <button className="btn-brut" data-testid={CANDIDATE.addSkill} onClick={addSkill}>
            <Plus size={16} /> Add
          </button>
        </div>
        {data.skills.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {data.skills.map((s, i) => (
              <span
                key={s}
                className="pill pill-flame"
                data-testid={CANDIDATE.skillTag(i)}
              >
                {s}
                <button
                  className="ml-1 hover:opacity-70"
                  onClick={() => removeSkill(i)}
                  aria-label="remove"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Certifications */}
      <div className="md:col-span-2">
        <div className="dashed-line mb-6" />
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] mb-3">
          Certifications
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <input
            className="input-brut md:col-span-2"
            data-testid={CANDIDATE.certName}
            placeholder="FTTH design & install"
            value={certDraft.name}
            onChange={(e) => setCertDraft((c) => ({ ...c, name: e.target.value }))}
          />
          <input
            className="input-brut"
            data-testid={CANDIDATE.certIssuer}
            placeholder="Issuer"
            value={certDraft.issuer}
            onChange={(e) => setCertDraft((c) => ({ ...c, issuer: e.target.value }))}
          />
          <input
            className="input-brut"
            data-testid={CANDIDATE.certYear}
            placeholder="Year"
            value={certDraft.year}
            onChange={(e) => setCertDraft((c) => ({ ...c, year: e.target.value }))}
          />
        </div>
        <div className="mt-3">
          <button className="btn-brut" data-testid={CANDIDATE.addCert} onClick={addCert}>
            <Plus size={16} /> Add certification
          </button>
        </div>
        {data.certifications.length > 0 && (
          <ul className="mt-4 divide-y-2 divide-black border-2 border-black">
            {data.certifications.map((c, i) => (
              <li
                key={`${c.name}-${c.year || ""}-${c.issuer || ""}-${i}`}
                className="p-3 flex items-center justify-between bg-[rgb(var(--paper-2))]"
              >
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="font-mono text-xs text-black/60">
                    {c.issuer || "—"} · {c.year || "—"}
                  </div>
                </div>
                <button
                  className="btn-brut btn-ghost text-xs"
                  onClick={() => removeCert(i)}
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StepBank({ data, set }) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Field label="Bank name" required>
        <input
          className="input-brut"
          data-testid={CANDIDATE.bankName}
          value={data.bank_name}
          onChange={(e) => set({ bank_name: e.target.value })}
        />
      </Field>
      <Field label="Account holder name" required>
        <input
          className="input-brut"
          data-testid={CANDIDATE.bankHolder}
          value={data.bank_account_holder}
          onChange={(e) => set({ bank_account_holder: e.target.value })}
        />
      </Field>
      <Field label="Account number" required>
        <input
          className="input-brut"
          data-testid={CANDIDATE.bankAccount}
          value={data.bank_account_number}
          onChange={(e) => set({ bank_account_number: e.target.value })}
        />
      </Field>
      <Field label="Routing / sort code">
        <input
          className="input-brut"
          data-testid={CANDIDATE.bankRouting}
          value={data.bank_routing_number}
          onChange={(e) => set({ bank_routing_number: e.target.value })}
        />
      </Field>
      <Field label="IBAN">
        <input
          className="input-brut"
          data-testid={CANDIDATE.bankIban}
          value={data.bank_iban}
          onChange={(e) => set({ bank_iban: e.target.value })}
        />
      </Field>
      <Field label="SWIFT / BIC">
        <input
          className="input-brut"
          data-testid={CANDIDATE.bankSwift}
          value={data.bank_swift}
          onChange={(e) => set({ bank_swift: e.target.value })}
        />
      </Field>
      <Field label="Tax ID / National insurance">
        <input
          className="input-brut"
          data-testid={CANDIDATE.taxId}
          value={data.tax_id}
          onChange={(e) => set({ tax_id: e.target.value })}
        />
      </Field>
    </div>
  );
}

function StepImmigration({ data, set, docDraft, setDocDraft, addDoc, removeDoc }) {
  return (
    <div className="grid gap-5">
      <div className="grid md:grid-cols-3 gap-5">
        <Field label="Immigration status" required>
          <select
            className="select-brut"
            data-testid={CANDIDATE.immStatus}
            value={data.immigration_status}
            onChange={(e) => set({ immigration_status: e.target.value })}
          >
            <option value="citizen">Citizen</option>
            <option value="permanent_resident">Permanent resident</option>
            <option value="work_visa">Work visa</option>
            <option value="student_visa">Student visa</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Work authorization expiry" hint="Leave blank if not applicable">
          <input
            type="date"
            className="input-brut"
            data-testid={CANDIDATE.immExpiry}
            value={data.work_authorization_expiry}
            onChange={(e) => set({ work_authorization_expiry: e.target.value })}
          />
        </Field>
        <Field label="Notes to reviewer">
          <input
            className="input-brut"
            data-testid={CANDIDATE.immNotes}
            value={data.immigration_notes}
            onChange={(e) => set({ immigration_notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      <div className="dashed-line" />

      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] mb-3">
          Supporting documents
        </div>
        <p className="text-sm text-black/70 mb-4">
          Attach identity & work-authorization proofs (passport, visa, work
          permit, national ID). PDF or images up to 5MB each.
        </p>

        <div className="grid md:grid-cols-4 gap-3 items-end">
          <Field label="Type" required>
            <select
              className="select-brut"
              data-testid={CANDIDATE.docKind}
              value={docDraft.kind}
              onChange={(e) => setDocDraft((d) => ({ ...d, kind: e.target.value }))}
            >
              <option value="passport">Passport</option>
              <option value="visa">Visa</option>
              <option value="work_permit">Work permit</option>
              <option value="national_id">National ID</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Label" required>
            <input
              className="input-brut"
              data-testid={CANDIDATE.docLabel}
              placeholder="e.g. Passport bio page"
              value={docDraft.label}
              onChange={(e) => setDocDraft((d) => ({ ...d, label: e.target.value }))}
            />
          </Field>
          <Field label="Expiry (optional)">
            <input
              type="date"
              className="input-brut"
              data-testid={CANDIDATE.docExpiry}
              value={docDraft.expiry_date}
              onChange={(e) => setDocDraft((d) => ({ ...d, expiry_date: e.target.value }))}
            />
          </Field>
          <Field label="File" required>
            <input
              id="apply-doc-file-input"
              type="file"
              accept=".pdf,image/*"
              data-testid={CANDIDATE.docFile}
              className="input-brut file:mr-3 file:border-0 file:bg-black file:text-[rgb(var(--paper))] file:px-3 file:py-1 file:font-mono file:uppercase file:text-xs"
              onChange={(e) => setDocDraft((d) => ({ ...d, file: e.target.files?.[0] || null }))}
            />
          </Field>
        </div>

        <div className="mt-3">
          <button className="btn-brut" data-testid={CANDIDATE.addDoc} onClick={addDoc}>
            <Upload size={16} /> Attach document
          </button>
        </div>

        {data.documents.length > 0 && (
          <ul className="mt-5 grid md:grid-cols-2 gap-3">
            {data.documents.map((d, i) => (
              <li
                key={`${d.file_name}-${i}`}
                data-testid={CANDIDATE.docItem(i)}
                className="brut-card p-4 flex items-center justify-between"
              >
                <div>
                  <div className="font-medium">{d.label}</div>
                  <div className="font-mono text-xs text-black/60">
                    {d.kind.replace("_", " ")} · {d.file_name}
                    {d.expiry_date ? ` · expires ${d.expiry_date}` : ""}
                  </div>
                </div>
                <button
                  data-testid={CANDIDATE.removeDoc(i)}
                  className="btn-brut btn-ghost text-xs"
                  onClick={() => removeDoc(i)}
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StepVehicle({ data, set }) {
  const truckPreview = data.truck_picture_file
    ? URL.createObjectURL(data.truck_picture_file)
    : null;
  const insurancePreview =
    data.insurance_certificate_file && data.insurance_certificate_file.type?.startsWith("image/")
      ? URL.createObjectURL(data.insurance_certificate_file)
      : null;

  const ready = !!data.truck_picture_file && !!data.insurance_certificate_file;

  return (
    <div className="grid gap-6">
      <div
        data-testid={CANDIDATE.vehicleReadiness}
        className={`brut-card p-4 flex items-center gap-3 ${ready ? "" : "bg-[rgb(var(--paper-2))]"}`}
      >
        <div
          className={`w-10 h-10 border-2 border-black grid place-items-center ${
            ready ? "bg-[rgb(var(--flame))]" : "bg-[rgb(var(--paper))]"
          }`}
        >
          <Upload size={18} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-black/60">
            Vehicle readiness
          </div>
          <div className="font-medium">
            {ready
              ? "Awaiting admin verification once you submit"
              : "Not ready — both files below are required"}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Truck picture" required hint="Clear photo of the truck you'll use on site · max 5MB">
          <input
            type="file"
            accept="image/*"
            data-testid={CANDIDATE.truckPicture}
            className="input-brut file:mr-3 file:border-0 file:bg-black file:text-[rgb(var(--paper))] file:px-3 file:py-1 file:font-mono file:uppercase file:text-xs"
            onChange={(e) => set({ truck_picture_file: e.target.files?.[0] || null })}
          />
          {truckPreview && (
            <div className="mt-3 border-2 border-black">
              <img src={truckPreview} alt="Truck preview" className="w-full h-52 object-cover" />
            </div>
          )}
        </Field>
        <div className="space-y-3">
          <Field label="Insurance certificate" required hint="Image or PDF · max 5MB">
            <input
              type="file"
              accept="image/*,.pdf"
              data-testid={CANDIDATE.insuranceCertificate}
              className="input-brut file:mr-3 file:border-0 file:bg-black file:text-[rgb(var(--paper))] file:px-3 file:py-1 file:font-mono file:uppercase file:text-xs"
              onChange={(e) => set({ insurance_certificate_file: e.target.files?.[0] || null })}
            />
          </Field>
          <Field label="Insurance expiry date" required>
            <input
              type="date"
              className="input-brut"
              data-testid="apply-insurance-expiry"
              value={data.insurance_expiry}
              onChange={(e) => set({ insurance_expiry: e.target.value })}
            />
          </Field>
          {insurancePreview ? (
            <div className="border-2 border-black">
              <img src={insurancePreview} alt="Insurance preview" className="w-full h-40 object-cover" />
            </div>
          ) : data.insurance_certificate_file ? (
            <div className="brut-card p-3 font-mono text-xs">
              📄 {data.insurance_certificate_file.name} · PDF
            </div>
          ) : null}
        </div>
      </div>

      <div className="dashed-line" />

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Driver's license (photo)" required hint="Front of your license · max 5MB">
          <input
            type="file"
            accept="image/*,.pdf"
            data-testid="apply-drivers-license"
            className="input-brut file:mr-3 file:border-0 file:bg-black file:text-[rgb(var(--paper))] file:px-3 file:py-1 file:font-mono file:uppercase file:text-xs"
            onChange={(e) => set({ drivers_license_file: e.target.files?.[0] || null })}
          />
          {data.drivers_license_file && (
            <div className="mt-3 font-mono text-xs">📄 {data.drivers_license_file.name}</div>
          )}
        </Field>
        <Field label="Driver's license expiry" required>
          <input
            type="date"
            className="input-brut"
            data-testid="apply-drivers-license-expiry"
            value={data.drivers_license_expiry}
            onChange={(e) => set({ drivers_license_expiry: e.target.value })}
          />
        </Field>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="py-1.5">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-black/55">
        {label}
      </div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}

function StepReview({ data }) {
  const projectLabel = data.project
    ? data.project.split("_").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" ")
    : "—";
  const roleLabel = data.role
    ? data.role.split("_").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" ")
    : "—";
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="md:col-span-2">
        <h3 className="font-display text-2xl mb-3">Assignment</h3>
        <div className="grid grid-cols-2 gap-x-4">
          <KV label="Project" value={projectLabel} />
          <KV label="Field / role" value={roleLabel} />
        </div>
        <div className="dashed-line my-4" />
      </div>
      <div>
        <h3 className="font-display text-2xl mb-3">Personal</h3>
        <div className="grid grid-cols-2 gap-x-4">
          <KV label="Full name" value={data.full_name} />
          <KV label="Email" value={data.email} />
          <KV label="Phone" value={data.phone} />
          <KV label="DOB" value={data.date_of_birth} />
          <KV label="Nationality" value={data.nationality} />
          <KV label="Country" value={data.country} />
        </div>
        <div className="dashed-line my-4" />
        <h3 className="font-display text-2xl mb-3">Emergency</h3>
        <div className="grid grid-cols-2 gap-x-4">
          <KV label="Contact" value={data.emergency_contact_name} />
          <KV label="Relation" value={data.emergency_contact_relation} />
          <KV label="Phone" value={data.emergency_contact_phone} />
        </div>
        <div className="dashed-line my-4" />
        <h3 className="font-display text-2xl mb-3">Professional</h3>
        <div className="grid grid-cols-2 gap-x-4">
          <KV label="Years exp." value={String(data.years_experience)} />
          <KV label="FTTH years" value={String(data.ftth_experience_years)} />
          <KV label="Previous role" value={data.previous_role} />
          <KV label="Own tools" value={data.has_own_tools ? "Yes" : "No"} />
        </div>
        <div className="mt-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-black/55 mb-1">
            Skills
          </div>
          {data.skills.length ? (
            <div className="flex flex-wrap gap-1.5">
              {data.skills.map((s) => (
                <span key={s} className="pill pill-muted">{s}</span>
              ))}
            </div>
          ) : (
            <span className="text-sm">—</span>
          )}
        </div>
        <div className="mt-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-black/55 mb-1">
            Certifications
          </div>
          {data.certifications.length ? (
            <ul className="text-sm list-disc pl-5">
              {data.certifications.map((c, i) => (
                <li key={`${c.name}-${c.year || ""}-${i}`}>{c.name} — {c.issuer || "—"} ({c.year || "—"})</li>
              ))}
            </ul>
          ) : "—"}
        </div>
      </div>

      <div>
        <h3 className="font-display text-2xl mb-3">Payroll</h3>
        <div className="grid grid-cols-2 gap-x-4">
          <KV label="Bank" value={data.bank_name} />
          <KV label="Holder" value={data.bank_account_holder} />
          <KV label="Account" value={data.bank_account_number} />
          <KV label="Routing" value={data.bank_routing_number} />
          <KV label="IBAN" value={data.bank_iban} />
          <KV label="Swift" value={data.bank_swift} />
          <KV label="Tax ID" value={data.tax_id} />
        </div>
        <div className="dashed-line my-4" />
        <h3 className="font-display text-2xl mb-3">Immigration</h3>
        <div className="grid grid-cols-2 gap-x-4">
          <KV label="Status" value={data.immigration_status.replace("_", " ")} />
          <KV label="Auth. expiry" value={data.work_authorization_expiry} />
        </div>
        <KV label="Notes" value={data.immigration_notes} />
        <div className="mt-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-black/55 mb-1">
            Documents ({data.documents.length})
          </div>
          {data.documents.length ? (
            <ul className="text-sm list-disc pl-5">
              {data.documents.map((d, i) => (
                <li key={`${d.file_name}-${i}`}>
                  {d.label} — {d.kind.replace("_", " ")} · {d.file_name}
                  {d.expiry_date ? ` · exp ${d.expiry_date}` : ""}
                </li>
              ))}
            </ul>
          ) : "—"}
        </div>
        <div className="mt-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-black/55 mb-1">
            Vehicle & insurance
          </div>
          <ul className="text-sm list-disc pl-5">
            <li>Truck picture: <b>{data.truck_picture_file ? data.truck_picture_file.name : "—"}</b></li>
            <li>Insurance certificate: <b>{data.insurance_certificate_file ? data.insurance_certificate_file.name : "—"}</b></li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---------- validation ----------
function validateStep(step, d) {
  const errs = [];
  if (step === 0) {
    if (!d.project) errs.push("Select a project");
    if (!d.role) errs.push("Select a field / role");
    if (!d.full_name.trim()) errs.push("Full name is required");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email)) errs.push("Valid email is required");
    if (!d.phone.trim()) errs.push("Phone is required");
    if (!d.date_of_birth) errs.push("Date of birth is required");
    if (!d.nationality.trim()) errs.push("Nationality is required");
    if (!d.address_line1.trim()) errs.push("Address line 1 is required");
    if (!d.city.trim()) errs.push("City is required");
    if (!d.country.trim()) errs.push("Country is required");
    if (!d.emergency_contact_name.trim()) errs.push("Emergency contact name is required");
    if (!d.emergency_contact_relation.trim()) errs.push("Emergency contact relation is required");
    if (!d.emergency_contact_phone.trim()) errs.push("Emergency contact phone is required");
  }
  if (step === 1) {
    if (d.years_experience === "" || Number(d.years_experience) < 0) errs.push("Years of experience is required");
    if (d.ftth_experience_years === "" || Number(d.ftth_experience_years) < 0) errs.push("FTTH years is required");
  }
  if (step === 2) {
    if (!d.bank_name.trim()) errs.push("Bank name is required");
    if (!d.bank_account_holder.trim()) errs.push("Account holder is required");
    if (!d.bank_account_number.trim()) errs.push("Account number is required");
  }
  if (step === 3) {
    if (!d.immigration_status) errs.push("Immigration status is required");
    if (d.immigration_status !== "citizen" && d.documents.length === 0)
      errs.push("Attach at least one supporting document");
  }
  if (step === 4) {
    if (!d.truck_picture_file) errs.push("Truck picture is required");
    if (!d.insurance_certificate_file) errs.push("Insurance certificate is required");
    if (!d.insurance_expiry) errs.push("Insurance expiry date is required");
    if (!d.drivers_license_file) errs.push("Driver's license photo is required");
    if (!d.drivers_license_expiry) errs.push("Driver's license expiry date is required");
    const MAX = 5 * 1024 * 1024;
    if (d.truck_picture_file && d.truck_picture_file.size > MAX)
      errs.push("Truck picture must be under 5MB");
    if (d.insurance_certificate_file && d.insurance_certificate_file.size > MAX)
      errs.push("Insurance certificate must be under 5MB");
    if (d.drivers_license_file && d.drivers_license_file.size > MAX)
      errs.push("Driver's license must be under 5MB");
  }
  return errs;
}
